<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import type { ClassValue } from "svelte/elements";
	import newsPlaceholder from "@core/assets/images/news-placeholder.svg";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { PublicCaseStudy } from "@core/schema/PublicCaseStudy.js";
	import { getNewsDetailPath } from "@core/schema/paths/getNewsDetailPath.js";
	import { formatDateDDMMYYYY } from "@core/utils/formatDateDDMMYYYY.js";
	import Button from "./common/Button.svelte";
	import Media from "./common/Media.svelte";

	const {
		caseStudy,
		class: customClass,
		hugeImage,
		horizontal,
	}: { caseStudy: PublicCaseStudy; class?: ClassValue; hugeImage?: boolean; horizontal?: boolean } = $props();
	const publishedDate = $derived(new Date(caseStudy.publishedAt));
	const formattedDate = $derived(formatDateDDMMYYYY(publishedDate));
</script>

<article class={[customClass, "bg-white/50 backdrop-blur rounded-3xl p-2 xl:p-4 h-full"]}>
	<a
		class={["flex gap-3 xl:gap-6 h-full group/underline flex-col", horizontal && "xs:flex-row xl:gap-10"]}
		href={getNewsDetailPath(caseStudy)}
	>
		<div
			class={[
				"relative rounded xl:rounded-lg overflow-hidden shrink-0 max-w-full bg-cmcbg",
				hugeImage
					? "w-full md:h-[30rem] md:w-min aspect-[19/12]"
					: "h-[7.125rem] xl:h-[14.25rem] w-full aspect-[909/511]",
				horizontal && "xs:shrink-0 xs:basis-1/2",
			]}
		>
			<Media
				source={caseStudy.primaryImage ?? {
					src: newsPlaceholder,
					type: "image/svg+xml",
				}}
				width={hugeImage ? 784 : 496}
				height={hugeImage ? 480 : 228}
				alt="Obrázek článku „{caseStudy.title}“"
				class="size-full object-cover"
				wrapperClass="size-full"
				sourcesets={{
					"<2xl": [hugeImage ? 500 : 235, 0],
					">2xl": [hugeImage ? 500 : 235, 0],
				}}
			/>
			{#if caseStudy.companyName}
				<div
					class="bg-white/75 backdrop-blur-sm text-[0.375rem] leading-none md:text-xs py-1 px-1.5 md:py-2 md:px-3 rounded font-semibold uppercase absolute left-1 top-1 xl:left-2 xl:top-2"
				>
					<Formatted text={caseStudy.companyName} />
				</div>
			{/if}
		</div>
		<div
			class={[
				"flex flex-col gap-2 xl:gap-3 grow justify-between",
				hugeImage && "max-h-[15.75rem] my-auto",
				horizontal && "xs:basis-1/2 xs:h-full",
				hugeImage && horizontal && "xs:max-h-[30rem] xs:my-0",
			]}
		>
			<div class={["gap-3 md:gap-5 xl:gap-6 flex flex-col grow", horizontal && "xs:contents"]}>
				<time
					datetime={publishedDate.toISOString()}
					class="text-2xs/[1] md:text-xs/[1] text-cmcsecondary font-semibold tracking-[0.02em] uppercase"
				>
					{formattedDate}
				</time>
				<!-- TODO CC-12: globalize heading styles (when the designer provides details) -->
				<div class={["flex-col flex gap-3 md:gap-4 grow", !horizontal && "xl:gap-6"]}>
					<h3 class="text-base xl:text-2xl leading-none font-bold text-cmcsecondary">
						<Formatted text={caseStudy.title} />
					</h3>
					<div class="relative grow overflow-hidden">
						<p class="font-medium text-2xs/[1.6] md:text-xs/[1.6] line-clamp-4 absolute inset-x-0 top-0 max-h-full">
							<Formatted text={caseStudy.perex} />
						</p>
					</div>
				</div>
			</div>
			<Button type="span" variant={ButtonVariant.Text} icon="arrow" class="mt-top !text-sm lg:!text-base">
				<Formatted text="hover[[Přečíst více]]" />
			</Button>
		</div>
	</a>
</article>
